import { type MouseEvent } from 'react';

import { ORIENTATION } from '../../../../constants.js';
import { useChartLayoutLoading } from '../../../chart-layout/hooks/useChartLayoutLoading.js';
import { useOrientation } from '../context/RangeLegendOrientationContext.js';
import { useScale } from '../context/RangeLegendScaleContext.js';
import { useSetHoveredMarkerId } from '../hooks/store/use-set-hovered-marker-id.js';

// https://en.wikipedia.org/wiki/Euclidean_distance
const distance = (point: number, pointToCheck: number) =>
  Math.sqrt(Math.pow(point - pointToCheck, 2));

/**
 * This hook is responsible for calculating the closest marker when
 * range legend overlay is hovered, using euclidian distance math,
 * setting the corresponding marker in order to show to the consumer the marker
 *
 * @param itemMarkers - a set of markers
 * @param overlayEl - the overlay element
 *
 * @returns Mouse movement handlers
 */
export const useLegendSegmentsOverlayHandlers = (itemMarkers: number[]) => {
  const { setHoveredMarkerId } = useSetHoveredMarkerId();
  const { scale } = useScale();
  const { orientation } = useOrientation();
  const isLoading = useChartLayoutLoading();
  const isHorizontal = orientation === ORIENTATION.Horizontal;

  const markersWithPosition = itemMarkers.map((marker) => ({
    marker,
    position: scale(marker),
  }));

  const onMouseMoveHandler = (event: MouseEvent<SVGRectElement>) => {
    const { clientX, clientY } = event;
    const { left, top } = event.currentTarget.getBoundingClientRect();
    const mousePosition = isHorizontal ? clientX - left : clientY - top;

    if (!isLoading) {
      const closest = markersWithPosition.reduce(
        (acc, cur) =>
          distance(acc.position, mousePosition) <
          distance(cur.position, mousePosition)
            ? acc
            : cur,
        markersWithPosition[0],
      );

      setHoveredMarkerId(closest.marker);
    }
  };

  const onMouseLeaveHandler = () => setHoveredMarkerId(null);

  return {
    onMouseMoveHandler,
    onMouseLeaveHandler,
  };
};
